import React, { useState, useEffect, useMemo } from 'react';
import {
  Menu,
  Search,
  Filter,
  MapPin,
  Activity,
  Car,
  Clock,
  Users,
  Navigation,
  Gauge,
  Map,
  Camera,
  MessageCircle,
  Eye,
  Fuel,
  Heart,
  Droplets,
  Wind,
  Thermometer,
  Zap,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  Maximize2
} from 'lucide-react';
import TrackingMap from '../maps/TrackingMap';
import { useLiveTrackingData } from '../../hooks/useBooking';
import { useBookingWebSocket } from '../../hooks/useWebSocket';
import webSocketService from '../../services/webSocketService';
import hospitalService from '../../services/hospitalService';

// Helper function to get status badge className
const getStatusBadgeClassName = (status) => {
  switch (status) {
    case 'On Call':
      return 'bg-orange-100 text-orange-700';
    case 'On Duty':
    case 'Available':
      return 'bg-green-100 text-green-700';
    case 'Pending':
      return 'bg-yellow-100 text-yellow-700';
    case 'Off Duty':
      return 'bg-gray-100 text-gray-700';
    default:
      return 'bg-orange-100 text-orange-700';
  }
};

// Helper function to get status indicator color
const getStatusIndicatorColor = (status) => {
  if (status === 'On Call' || status === 'Available' || status === 'On Duty') {
    return status === 'On Call' ? 'bg-orange-500' : 'bg-green-500';
  }
  return 'bg-yellow-500';
};

// Helper function to get status display text
const getStatusDisplayText = (status) => {
  return status === 'On Call' ? 'Active' : status;
};

// Wrapper component to fetch tracking data and render TrackingMap for SAdminDashboard
// Memoized to prevent unnecessary re-renders
const TrackingMapWrapper = React.memo(({ booking, bookingId }) => {
  const [realtimeDriverLocation, setRealtimeDriverLocation] = React.useState(null);
  
  // Use ref to track if this is the first render (for initial map load only)
  const isFirstRender = React.useRef(true);
  
  React.useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
    }
  }, [bookingId]);
  
  // WebSocket for real-time updates
  const { locationUpdates } = useBookingWebSocket(bookingId);
  
  // Poll driver location every 3 seconds for real-time updates (fallback)
  const { data: trackingData } = useLiveTrackingData(bookingId, 3000);
  
  // Listen for WebSocket location updates
  React.useEffect(() => {
    if (!bookingId) return;

    const handleLocationUpdate = (data) => {
      if ((data.bookingId === bookingId || data.booking_id === bookingId) && data.latitude && data.longitude) {
        const lat = parseFloat(data.latitude);
        const lng = parseFloat(data.longitude);
        
        if (!isNaN(lat) && !isNaN(lng) && isFinite(lat) && isFinite(lng) &&
            lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
          setRealtimeDriverLocation({
            latitude: lat,
            longitude: lng,
            lastUpdated: data.timestamp || new Date().toISOString(),
            heading: data.heading || null,
            bearing: data.bearing || null
          });
        }
      }
    };

    webSocketService.on('driver_location_update', handleLocationUpdate);
    webSocketService.on('location_update', handleLocationUpdate);

    return () => {
      webSocketService.off('driver_location_update', handleLocationUpdate);
      webSocketService.off('location_update', handleLocationUpdate);
    };
  }, [bookingId]);

  // Process location updates from hook
  React.useEffect(() => {
    if (locationUpdates && locationUpdates.length > 0) {
      const latest = locationUpdates[locationUpdates.length - 1];
      if (latest.latitude && latest.longitude) {
        const lat = parseFloat(latest.latitude);
        const lng = parseFloat(latest.longitude);
        if (!isNaN(lat) && !isNaN(lng) && isFinite(lat) && isFinite(lng)) {
          setRealtimeDriverLocation({
            latitude: lat,
            longitude: lng,
            lastUpdated: latest.timestamp || new Date().toISOString(),
            heading: latest.heading || null,
            bearing: latest.bearing || null
          });
        }
      }
    }
  }, [locationUpdates]);
  
  // Removed excessive logging - only log significant changes
  
  // Extract location data from booking - handle multiple possible data structures
  // These locations are STATIC and extracted only once from the booking object
  const extractLocation = (locationObj, addressFallback) => {
    if (!locationObj) return null;
    
    // Try multiple possible coordinate field names
    const lat = locationObj.latitude || locationObj.lat || locationObj.lat_coordinate || locationObj.lat_coord;
    const lng = locationObj.longitude || locationObj.lng || locationObj.lng_coordinate || locationObj.lng_coord || locationObj.lon;
    const address = locationObj.address || addressFallback || 'Location';
    
    const numLat = Number(lat);
    const numLng = Number(lng);
    
    // Validate coordinates
    if (isNaN(numLat) || isNaN(numLng) || !isFinite(numLat) || !isFinite(numLng)) {
      return null;
    }
    
    if (numLat < -90 || numLat > 90 || numLng < -180 || numLng > 180) {
      return null;
    }
    
    return {
      latitude: numLat,
      longitude: numLng,
      address: address
    };
  };
  
  // STATIC pickup and dropoff locations - extracted only once from booking object
  // These do NOT change when tracking data updates
  const pickupLocation = useMemo(() => {
    return extractLocation(
      booking.pickupLocation || booking.pickup_location || booking.pickupLocationData,
      booking.pickupAddress || booking.pickup_address || booking.pickupAddress || 'Pickup Location'
    );
  }, [booking.pickupLocation, booking.pickup_location, booking.pickupLocationData, booking.pickupAddress, booking.pickup_address]);
  
  const dropoffLocation = useMemo(() => {
    return extractLocation(
      booking.dropoffLocation || booking.dropoff_location || booking.dropoffLocationData || booking.drop_location,
      booking.dropoffAddress || booking.dropoff_address || booking.dropAddress || 'Dropoff Location'
    );
  }, [booking.dropoffLocation, booking.dropoff_location, booking.dropoffLocationData, booking.drop_location, booking.dropoffAddress, booking.dropoff_address, booking.dropAddress]);
  
  // Extract DRIVER location from tracking data
  // Priority: WebSocket real-time > Polling data
  // Use ref to track previous location and avoid unnecessary updates
  const prevAmbulanceLocationRef = React.useRef(null);
  
  const ambulanceLocation = useMemo(() => {
    // PRIORITY 1: Real-time WebSocket location
    if (realtimeDriverLocation?.latitude && realtimeDriverLocation?.longitude) {
      return realtimeDriverLocation;
    }
    
    // Prioritize driverLocation, fallback to ambulanceLocation
    const driverLoc = trackingData?.data?.tracking?.driverLocation || trackingData?.data?.tracking?.ambulanceLocation;
    
    if (!driverLoc) {
      return null;
    }
    
    // Handle both string and number coordinates from API
    const latRaw = driverLoc.latitude;
    const lngRaw = driverLoc.longitude;
    
    if (latRaw == null || lngRaw == null) {
      return null;
    }
    
    // Convert to numbers (handles both string and number types)
    const lat = typeof latRaw === 'string' ? parseFloat(latRaw) : Number(latRaw);
    const lng = typeof lngRaw === 'string' ? parseFloat(lngRaw) : Number(lngRaw);
    
    // Validate coordinates
    if (isNaN(lat) || isNaN(lng) || !isFinite(lat) || !isFinite(lng)) {
      return null;
    }
    
    if (lat < -90 || lat > 90 || lng < -180 || lng > 180) {
      return null;
    }
    
    // Check if location actually changed (avoid creating new object if same)
    const prevLoc = prevAmbulanceLocationRef.current;
    if (prevLoc && prevLoc.latitude === lat && prevLoc.longitude === lng) {
      return prevLoc; // Return same reference to prevent re-render
    }
    
    // Return exact coordinates from API response
    const location = {
      latitude: lat,
      longitude: lng,
      lastUpdated: driverLoc.lastUpdated || null,
      heading: driverLoc.heading || null,
      bearing: driverLoc.bearing || null
    };
    
    prevAmbulanceLocationRef.current = location;
    return location;
  }, [realtimeDriverLocation, trackingData?.data?.tracking?.driverLocation, trackingData?.data?.tracking?.ambulanceLocation]);

  // Debug logging removed to prevent excessive console output

  const distance = trackingData?.data?.tracking?.distance || null;
  const eta = trackingData?.data?.tracking?.eta || null;
  const status = trackingData?.data?.status || trackingData?.status || booking.status;

  // Always render the map - no loading states or error messages
  return (
    <div className="relative w-full h-full">
      <TrackingMap
        pickupLocation={pickupLocation}
        dropoffLocation={dropoffLocation}
        ambulanceLocation={ambulanceLocation}
        distance={distance}
        eta={eta}
        status={status}
        journeyDirection={booking.journey_direction || booking.journeyDirection || booking.direction || 'OUTBOUND'}
        zoom={14}
        showControls={false}
        height="100%"
      />
    </div>
  );
}, (prevProps, nextProps) => {
  // Custom comparison for React.memo - only re-render if booking or bookingId changed
  return prevProps.bookingId === nextProps.bookingId && 
         prevProps.booking === nextProps.booking;
});

// Helper function to get sensor value color based on thresholds
const getSensorValueColor = (value, thresholds) => {
  const { high, medium } = thresholds;
  if (value >= high) return 'text-green-600';
  if (value >= medium) return 'text-yellow-600';
  return 'text-red-600';
};

// Helper function to get sensor color for reverse logic (lower is better, e.g., alcohol)
const getReverseSensorValueColor = (value, thresholds) => {
  const { low, medium } = thresholds;
  if (value <= low) return 'text-green-600';
  if (value <= medium) return 'text-yellow-600';
  return 'text-red-600';
};

// Helper function to render sensor data item
const renderSensorDataItem = (label, value, colorFn, thresholds) => {
  const colorClass = colorFn(value || 0, thresholds);
  return (
    <div className="flex items-center space-x-1" key={label}>
      <span className="text-gray-600">{label}</span>
      <span className={`font-bold ${colorClass}`}>
        {value || 0}%
      </span>
    </div>
  );
};

const SAdminDashboard = ({ showHeader = true, hideMetricsCards = false, currentPage = 1, itemsPerPage = 6, onPaginationChange, dateFrom, dateTo }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedAmbulance, setSelectedAmbulance] = useState(null);
  const [expandedAmbulance, setExpandedAmbulance] = useState(null);
  const [activeBookings, setActiveBookings] = useState([]);
  const [trackingData, setTrackingData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch active bookings using the new active bookings API
  React.useEffect(() => {
    const fetchActiveBookings = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch active bookings from the new endpoint: /v1/hospital/bookings/active
        const params = {
          page: currentPage,
          limit: itemsPerPage,
          ...(dateFrom && { date_from: dateFrom instanceof Date ? dateFrom.toISOString() : dateFrom }),
          ...(dateTo && { date_to: dateTo instanceof Date ? dateTo.toISOString() : dateTo })
        };
        const bookingsResponse = await hospitalService.getActiveBookings(params);

        if (!bookingsResponse.success || !bookingsResponse.data) {
          throw new Error('Failed to fetch active bookings');
        }

        const bookings = Array.isArray(bookingsResponse.data)
          ? bookingsResponse.data
          : [];

        setActiveBookings(bookings);
        
        // Update pagination state
        if (onPaginationChange) {
          const totalPages = Math.ceil(bookings.length / itemsPerPage) || 1;
          onPaginationChange({
            page: currentPage,
            limit: itemsPerPage,
            total: bookings.length,
            totalPages: totalPages
          });
        }

        // Optionally fetch tracking data for real-time location updates
        // The active bookings API already includes vehicle and driver info
        const newTrackingData = {};
        const trackingPromises = bookings.map(async (booking) => {
          try {
            const bookingId = booking.booking_id || booking.id;
            if (!bookingId) return;

            // Fetch tracking data for real-time location/eta updates
            const trackingResponse = await hospitalService.getBookingTracking(bookingId);

            if (trackingResponse.success && trackingResponse.data) {
              newTrackingData[bookingId] = trackingResponse.data;
            }
          } catch (trackingError) {
            // Silently fail for individual tracking errors - booking data is already available
          }
        });

        await Promise.all(trackingPromises);
        setTrackingData(newTrackingData);

        setLoading(false);
      } catch (err) {
        console.error('âŒ [SAdminDashboard] Error fetching active bookings:', err);
        setError(err.message || 'Failed to load active bookings');
        setLoading(false);
      }
    };

    fetchActiveBookings();

    // Refetch when date range or pagination changes
    // Automatic refresh disabled - data will only refresh on component mount or when dependencies change
    // Users can manually refresh by navigating or using manual refresh actions
  }, [currentPage, itemsPerPage, onPaginationChange, dateFrom, dateTo]);

  // Mock data generator for vehicle sensor readings
  // This generates consistent mock data based on vehicle/booking ID to simulate sensor readings
  const generateMockSensorData = React.useCallback((vehicleId, bookingId) => {
    // Use vehicle ID or booking ID as seed for consistent data
    const seed = vehicleId || bookingId || Math.random().toString();
    
    // Convert seed to a numeric value for consistent generation
    let seedValue = 0;
    if (typeof seed === 'string') {
      seedValue = seed.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    } else if (typeof seed === 'number') {
      seedValue = seed;
    } else {
      seedValue = Math.abs(parseInt(String(seed).replace(/\D/g, '')) || 12345);
    }
    
    // Generate consistent but varied sensor readings based on seed
    const generateValue = (min, max, offset = 0) => {
      // Use a simple hash-like function to generate consistent values
      const hash = ((seedValue + offset) * 9301 + 49297) % 233280;
      const random = hash / 233280;
      return Math.max(min, Math.min(max, Math.round(min + (max - min) * random)));
    };

    return {
      oxygen: generateValue(85, 100, 0),        // 85-100%
      alcohol: generateValue(0, 5, 1),          // 0-5% (low is good)
      fuel: generateValue(20, 100, 2),          // 20-100%
      med1: generateValue(80, 100, 3),          // 80-100%
      med2: generateValue(75, 100, 4),          // 75-100%
      med3: generateValue(80, 100, 5),          // 80-100%
      med4: generateValue(85, 100, 6),          // 85-100%
    };
  }, []);

  // Transform active bookings into ambulance card format using new API response structure
  const transformBookingToAmbulance = (booking, index) => {
    const bookingId = booking.booking_id || booking.id;
    const tracking = trackingData[bookingId];

    // Map status to display format
    const getStatusDisplay = (status) => {
      if (!status) return 'PENDING';
      const statusUpper = status.toUpperCase();
      if (statusUpper === 'CONFIRMED') return 'On Call';
      if (statusUpper === 'PENDING') return 'Pending';
      if (statusUpper === 'IN_PROGRESS' || statusUpper === 'IN PROGRESS') return 'On Duty';
      if (statusUpper === 'COMPLETED') return 'Available';
      if (statusUpper === 'CANCELLED') return 'Off Duty';
      return status;
    };

    // Extract vehicle plate number or use booking ID as fallback
    const registration = booking.vehicle?.plate_number 
      ? booking.vehicle.plate_number 
      : `Booking #${bookingId}`;

    // Extract vehicle type
    const vehicleType = booking.vehicle?.type || 'Emergency';

    // Extract driver information (support nested driver object and flat driver_name / driver_phone)
    const driverName = booking.driver?.name || booking.driver_name || 'Assigned Driver';
    const driverPhone = booking.driver?.phone || booking.driver?.contact_phone || booking.driver?.phone_number || booking.driver_phone || booking.driver_contact || null;

    // Extract patient information
    const patientName = booking.patient_name || booking.patient_info?.name || 'Patient';
    const patientPhone = booking.patient_info?.contact_phone || 'N/A';

    // Extract location information (support nested objects and flat pickup_address / dropoff_address)
    const pickupAddress = booking.pickup_location?.address
      || booking.pickup_address
      || booking.pickupAddress
      || (typeof booking.pickup_location === 'string' ? booking.pickup_location : null)
      || 'N/A';
    const dropoffAddress = booking.dropoff_location?.address
      || booking.dropoff_address
      || booking.dropoffAddress
      || booking.drop_location?.address
      || booking.drop_address
      || booking.dropAddress
      || (typeof booking.dropoff_location === 'string' ? booking.dropoff_location : null)
      || (typeof booking.drop_location === 'string' ? booking.drop_location : null)
      || 'N/A';

    // Get status from tracking (most up-to-date) or booking
    // Tracking status takes precedence as it's updated in real-time by the driver
    const bookingStatus = tracking?.status || booking.status || 'PENDING';
    const displayStatus = getStatusDisplay(bookingStatus);

    // Extract tracking data if available
    const etaMinutes = tracking?.tracking?.eta 
      ? Math.round((new Date(tracking.tracking.eta) - new Date()) / 60000)
      : null;
    const distanceKm = tracking?.tracking?.distance || null;

    // Generate mock sensor data for this vehicle
    const vehicleId = booking.vehicle?.id;
    const mockSensorData = generateMockSensorData(vehicleId, bookingId);

    return {
      id: bookingId,
      registration: registration,
      status: displayStatus,
      location: pickupAddress,
      driver: driverName,
      driverPhone: driverPhone,
      type: vehicleType,
      lastUpdate: booking.updated_at 
        ? new Date(booking.updated_at).toLocaleString() 
        : booking.created_at 
          ? new Date(booking.created_at).toLocaleString() 
          : new Date().toLocaleString(),
      operationalTime: etaMinutes ? `${etaMinutes} min ETA` : 'N/A',
      operationalStatus: displayStatus,
      todayDistance: distanceKm ? `${distanceKm} km` : 'N/A',
      patientName: patientName,
      patientPhone: patientPhone,
      pickupAddress: pickupAddress,
      dropoffAddress: dropoffAddress,
      bookingStatus: bookingStatus, // Store original status
      vehicleId: booking.vehicle?.id || null,
      driverId: booking.driver?.id || null,
      notes: booking.notes || null,
      // Mock sensor data for vehicle details
      sensorData: {
        oxygen: mockSensorData.oxygen,
        alcohol: mockSensorData.alcohol,
        fuel: mockSensorData.fuel,
        med1: mockSensorData.med1,
        med2: mockSensorData.med2,
        med3: mockSensorData.med3,
        med4: mockSensorData.med4,
      },
      // Sensor status indicators - default to green for real bookings
      driverCamera: 'green',
      patientCamera: 'green',
      fuelData: 'green',
      patientMonitor: 'green',
      infusionPump: 'green',
      ventilator: 'green',
      vitals: 'green',
      airQuality: 'green',
      oxygenSensor: 'green',
      breathAnalyzer: 'green'
    };
  };

  // Calculate metrics from active bookings
  const metrics = React.useMemo(() => {
    const totalBookings = activeBookings.length;
    const confirmedBookings = activeBookings.filter(b => 
      b.status === 'CONFIRMED' || b.status === 'IN_PROGRESS' || b.status === 'IN PROGRESS'
    ).length;
    const pendingBookings = activeBookings.filter(b => b.status === 'PENDING').length;
    const cancelledBookings = activeBookings.filter(b => b.status === 'CANCELLED').length;

    return {
      vehicles: totalBookings || 0,
      onDuty: confirmedBookings || 0,
      offDuty: cancelledBookings || 0,
      activeRides: totalBookings || 0
    };
  }, [activeBookings]);

  // Show only active bookings; empty when none
  const ambulances = activeBookings.map(transformBookingToAmbulance);
  const getStatusColor = (status) => {
    switch (status) {
      case 'On Duty': return 'bg-green-100 text-green-800 border-green-200';
      case 'Available': return 'bg-green-100 text-green-800 border-green-200';
      case 'On Call': return 'bg-orange-100 text-orange-800 border-orange-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getBatteryColor = (battery) => {
    if (battery >= 80) return 'text-green-600';
    if (battery >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getStatusIndicatorColor = (status) => {
    switch (status) {
      case 'green': return 'bg-green-500';
      case 'yellow': return 'bg-yellow-500';
      case 'red': return 'bg-red-500';
      case 'gray': return 'bg-gray-500';
      default: return 'bg-gray-500';
    }
  };

  const getSensorIcon = (sensorType) => {
    switch (sensorType) {
      case 'fuelData': return <Fuel className="h-4 w-4" />;
      case 'patientMonitor': return <Heart className="h-4 w-4" />;
      case 'infusionPump': return <Droplets className="h-4 w-4" />;
      case 'ventilator': return <Wind className="h-4 w-4" />;
      case 'vitals': return <Thermometer className="h-4 w-4" />;
      case 'airQuality': return <Zap className="h-4 w-4" />;
      case 'oxygenSensor': return <Wind className="h-4 w-4" />;
      case 'breathAnalyzer': return <AlertCircle className="h-4 w-4" />;
      default: return <Activity className="h-4 w-4" />;
    }
  };


  const toggleAccordion = (id) => {
    setExpandedAmbulance(expandedAmbulance === id ? null : id);
  };

  // Get dynamic background color based on booking status
  const getStatusBackgroundColor = (bookingStatus, displayStatus) => {
    // Prefer original booking status, fallback to display status
    const status = (bookingStatus || displayStatus || '').toUpperCase().trim();
    
    // Map statuses to appropriate background colors
    // Priority: Check original status first, then display status
    
    // PENDING status - Yellow/Amber background
    if (status === 'PENDING') {
      return 'bg-gradient-to-r from-yellow-50 via-yellow-50 to-white border-yellow-300';
    }
    
    // CONFIRMED / On Call status - Orange background
    if (status === 'CONFIRMED' || status === 'ON CALL' || status.includes('CONFIRMED')) {
      return 'bg-gradient-to-r from-orange-50 via-orange-50 to-white border-orange-300';
    }
    
    // IN_PROGRESS / On Duty status - Green background
    if (status === 'IN_PROGRESS' || status === 'IN PROGRESS' || status === 'ON DUTY' || status.includes('PROGRESS')) {
      return 'bg-gradient-to-r from-green-50 via-green-50 to-white border-green-300';
    }
    
    // COMPLETED / Available status - Green background
    if (status === 'COMPLETED' || status === 'AVAILABLE' || status.includes('COMPLETED')) {
      return 'bg-gradient-to-r from-green-50 via-green-50 to-white border-green-300';
    }
    
    // CANCELLED / Off Duty status - Red background
    if (status === 'CANCELLED' || status === 'OFF DUTY' || status.includes('CANCELLED')) {
      return 'bg-gradient-to-r from-red-50 via-red-50 to-white border-red-300';
    }
    
    // Default to yellow for unknown statuses (likely pending)
    return 'bg-gradient-to-r from-yellow-50 via-yellow-50 to-white border-yellow-200';
  };

  // Different gradient colors for each ambulance (kept as fallback)
  const getGradientColor = (index) => {
    const gradients = [
      'bg-gradient-to-r from-orange-50 via-orange-50 to-white border-orange-200',
      'bg-gradient-to-r from-blue-50 via-blue-50 to-white border-blue-200',
      'bg-gradient-to-r from-green-50 via-green-50 to-white border-green-200',
      'bg-gradient-to-r from-purple-50 via-purple-50 to-white border-purple-200',
      'bg-gradient-to-r from-pink-50 via-pink-50 to-white border-pink-200',
    ];
    return gradients[index % gradients.length];
  };

  return (
    <div className={showHeader ? "min-h-screen bg-gray-50" : ""}>
      {/* Header Section */}
      {showHeader && (
        <header className="bg-white shadow-sm border-b border-gray-200">
          <div className="px-3 sm:px-4 lg:px-6 xl:px-8">
            <div className="flex items-center justify-between h-14 sm:h-16">
              {/* Left: Hamburger Menu */}
              <div className="flex items-center">
                <button className="p-1.5 sm:p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500">
                  <Menu className="h-5 w-5 sm:h-6 sm:w-6" />
                </button>
              </div>

              {/* Center: Title */}
              <div className="flex-1 flex justify-center">
                <h1 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900">S-Admin</h1>
              </div>

              {/* Right: Filters */}
              <div className="flex items-center">
                <button className="flex items-center px-2 sm:px-3 lg:px-4 py-1.5 sm:py-2 border border-gray-300 rounded-md text-xs sm:text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
                  <Filter className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
                  <span className="hidden sm:inline">Filters</span>
                </button>
              </div>
            </div>
          </div>
        </header>
      )}

      {/* Key Metrics Section */}
      {!hideMetricsCards && (
        <div className="bg-white shadow-sm border-b border-gray-200 rounded-lg">
          <div className={showHeader ? "px-3 sm:px-4 lg:px-6 xl:px-8 py-4 sm:py-6" : "px-3 sm:px-4 lg:px-6 py-3 sm:py-4"}>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6">
              {/* Vehicles */}
              <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg p-3 sm:p-4 border border-blue-200">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <Car className="h-6 w-6 sm:h-7 sm:w-7 lg:h-8 lg:w-8 text-blue-600" />
                  </div>
                  <div className="ml-2 sm:ml-3 lg:ml-4">
                    <p className="text-xs sm:text-sm font-medium text-blue-600">Vehicles</p>
                    <p className="text-lg sm:text-xl lg:text-2xl font-bold text-blue-900">{metrics.vehicles}</p>
                  </div>
                </div>
              </div>

              {/* On Duty */}
              <div className="bg-gradient-to-r from-green-50 to-green-100 rounded-lg p-3 sm:p-4 border border-green-200">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <Activity className="h-6 w-6 sm:h-7 sm:w-7 lg:h-8 lg:w-8 text-green-600" />
                  </div>
                  <div className="ml-2 sm:ml-3 lg:ml-4">
                    <p className="text-xs sm:text-sm font-medium text-green-600">On Duty</p>
                    <p className="text-lg sm:text-xl lg:text-2xl font-bold text-green-900">{metrics.onDuty}</p>
                  </div>
                </div>
              </div>

              {/* Off Duty */}
              <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-lg p-3 sm:p-4 border border-gray-200">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <Clock className="h-6 w-6 sm:h-7 sm:w-7 lg:h-8 lg:w-8 text-gray-600" />
                  </div>
                  <div className="ml-2 sm:ml-3 lg:ml-4">
                    <p className="text-xs sm:text-sm font-medium text-gray-600">Off Duty</p>
                    <p className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900">{metrics.offDuty}</p>
                  </div>
                </div>
              </div>

              {/* Active Rides */}
              <div className="bg-gradient-to-r from-purple-50 to-purple-100 rounded-lg p-3 sm:p-4 border border-purple-200">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <Users className="h-6 w-6 sm:h-7 sm:w-7 lg:h-8 lg:w-8 text-purple-600" />
                  </div>
                  <div className="ml-2 sm:ml-3 lg:ml-4">
                    <p className="text-xs sm:text-sm font-medium text-purple-600">Active Rides</p>
                    <p className="text-lg sm:text-xl lg:text-2xl font-bold text-purple-900">{metrics.activeRides}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Content Area */}
      <div className={showHeader ? "px-3 sm:px-4 lg:px-6 xl:px-8 py-4 sm:py-6" : "py-3 sm:py-4"}>
        <div className="grid grid-cols-1 gap-4 sm:gap-6">

          {/* Loading State */}
          {loading && (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <Activity className="h-8 w-8 animate-spin mx-auto mb-4 text-blue-600" />
                <p className="text-gray-600">Loading active bookings...</p>
              </div>
            </div>
          )}

          {/* Error State */}
          {error && !loading && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center">
                <AlertCircle className="h-5 w-5 text-red-600 mr-2" />
                <div>
                  <h3 className="text-sm font-medium text-red-800">Error loading bookings</h3>
                  <p className="text-sm text-red-600 mt-1">{error}</p>
                </div>
              </div>
            </div>
          )}

          {/* Left Column - Ambulance Information Panel */}
          {!loading && !error && (
            <div className="space-y-4 sm:space-y-6">
              {/* Ambulance Info Sections */}
              <div className="space-y-3 sm:space-y-4">
                {ambulances.length === 0 ? (
                  <div className="text-center py-12">
                    <Car className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                    <p className="text-gray-600">No active bookings found</p>
                  </div>
                ) : (
                  ambulances
                    .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
                    .map((ambulance, index) => {
                // Find the original booking from activeBookings
                const originalBooking = activeBookings.find(
                  b => (b.booking_id || b.id) === ambulance.id
                );

                // Helper function to render sensor data grid
                const renderSensorDataGrid = () => {
                  const sensorData = ambulance.sensorData || {};
                  return (
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-2 sm:gap-4 text-xs sm:text-sm">
                      {renderSensorDataItem('Oxygen', sensorData.oxygen, getSensorValueColor, { high: 95, medium: 90 })}
                      {renderSensorDataItem('Alcohol', sensorData.alcohol, getReverseSensorValueColor, { low: 2, medium: 4 })}
                      {renderSensorDataItem('Fuel', sensorData.fuel, getSensorValueColor, { high: 50, medium: 25 })}
                      {renderSensorDataItem('Med 1', sensorData.med1, getSensorValueColor, { high: 90, medium: 80 })}
                      {renderSensorDataItem('Med 2', sensorData.med2, getSensorValueColor, { high: 90, medium: 80 })}
                      {renderSensorDataItem('Med 3', sensorData.med3, getSensorValueColor, { high: 90, medium: 80 })}
                      {renderSensorDataItem('Med 4', sensorData.med4, getSensorValueColor, { high: 90, medium: 80 })}
                    </div>
                  );
                };

                return (
                  <div key={ambulance.id} className={`rounded-lg sm:rounded-xl shadow-sm border transition-all duration-200 overflow-hidden ${getStatusBackgroundColor(ambulance.bookingStatus, ambulance.status)}`}>
                    {/* Main Card - All Info Visible */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 sm:gap-4 p-3 sm:p-4">
                      {/* Left Column - Vehicle & Driver Info */}
                      <div className="lg:col-span-2 space-y-2 sm:space-y-3">
                        {/* Vehicle Registration */}
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-1 sm:space-x-2">
                            <Car className="h-3 w-3 sm:h-4 sm:w-4 text-gray-600" />
                            <span className="font-bold text-xs sm:text-sm text-gray-900">{ambulance.registration}</span>
                            {ambulance.type && (
                              <span className="text-xs text-gray-500 hidden sm:inline">{ambulance.type}</span>
                            )}
                          </div>
                          <button className="p-1 hover:bg-gray-200 rounded">
                            <Eye className="h-3 w-3 sm:h-4 sm:w-4 text-gray-600" />
                          </button>
                        </div>

                        {/* Time, Distance, Status */}
                        <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-xs text-gray-600">
                          <div className="flex items-center space-x-1">
                            <Clock className="h-3 w-3" />
                            <span>{ambulance.lastUpdate.split(',')[1]?.trim() || ambulance.lastUpdate}</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <Navigation className="h-3 w-3" />
                            <span>{ambulance.todayDistance}</span>
                          </div>
                          {ambulance.operationalTime && ambulance.operationalTime !== 'N/A' && (
                            <div className="flex items-center space-x-1">
                              <Clock className="h-3 w-3" />
                              <span>{ambulance.operationalTime}</span>
                            </div>
                          )}
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeClassName(ambulance.status)}`}>
                            {ambulance.status}
                          </span>
                          {ambulance.type && (
                            <span className="text-xs font-medium hidden sm:inline">{ambulance.type}</span>
                          )}
                        </div>

                        {/* Patient Info */}
                        <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4 space-y-2 sm:space-y-0">
                          <div className="flex items-center space-x-2">
                            <Users className="h-3 w-3 sm:h-4 sm:w-4 text-gray-600" />
                            <span className="font-semibold text-xs sm:text-sm">
                              {ambulance.patientName}
                              {ambulance.id && (
                                <span className="text-gray-500 font-normal ml-1">#{ambulance.id}</span>
                              )}
                            </span>
                          </div>
                          <div className="flex flex-wrap gap-2 sm:gap-4">
                            {ambulance.patientPhone && ambulance.patientPhone !== 'N/A' && (
                              <a 
                                href={`tel:${ambulance.patientPhone}`}
                                className="text-xs text-blue-600 hover:underline"
                              >
                                Call Now
                              </a>
                            )}
                            <button className="text-xs text-gray-600 hover:underline">History</button>
                            <button className="text-xs text-gray-600 hover:underline">View Profile</button>
                          </div>
                          <div className="flex items-center justify-between sm:ml-auto">
                            <span className="flex items-center space-x-1">
                              <div className={`w-2 h-2 rounded-full ${getStatusIndicatorColor(ambulance.status)}`}></div>
                              <span className="text-xs font-medium">{getStatusDisplayText(ambulance.status)}</span>
                            </span>
                            <button className="p-1 ml-2"><Eye className="h-3 w-3 sm:h-4 sm:w-4 text-gray-600" /></button>
                          </div>
                        </div>

                        {/* Driver Details */}
                        <div className="space-y-2 bg-white/50 rounded-lg p-2 sm:p-3">
                          <h4 className="font-semibold text-xs sm:text-sm flex items-center mb-2">
                            <Users className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
                            Driver Details
                          </h4>
                          <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4 space-y-2 sm:space-y-0">
                            <div className="flex items-center space-x-2">
                              <span className="font-semibold text-xs sm:text-sm">
                                {ambulance.driver}
                              </span>
                            {ambulance.driverPhone && (
                              <span className="text-xs text-gray-600 font-normal">
                                ({ambulance.driverPhone})
                              </span>
                            )}
                            {!ambulance.driverPhone && (
                              <span className="text-xs text-gray-400 font-normal">
                                (No contact)
                              </span>
                            )}
                          </div>
                          <div className="flex flex-wrap gap-2 sm:gap-4">
                            {ambulance.driverPhone && (
                              <a 
                                href={`tel:${ambulance.driverPhone}`}
                                className="text-xs text-blue-600 hover:underline"
                              >
                                Call Now
                              </a>
                            )}
                            <button className="text-xs text-gray-600 hover:underline">History</button>
                            <button className="text-xs text-gray-600 hover:underline">View Profile</button>
                          </div>
                          <div className="flex items-center space-x-2 text-xs sm:ml-auto">
                            <Clock className="h-3 w-3 text-gray-500" />
                            <span className="text-gray-600 hidden sm:inline">Last Update: {ambulance.lastUpdate}</span>
                            <Eye className="h-3 w-3 sm:h-4 sm:w-4 text-gray-600" />
                          </div>
                          </div>
                        </div>

                        {/* Location Information */}
                        <div className="space-y-2 bg-white/50 rounded-lg p-2 sm:p-3">
                          <h4 className="font-semibold text-xs sm:text-sm flex items-center mb-2">
                            <MapPin className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
                            Location Details
                          </h4>
                          <div className="space-y-1 text-xs">
                            <div className="flex items-start">
                              <span className="font-medium text-gray-700 min-w-[80px]">Pickup:</span>
                              <span className="text-gray-600 flex-1">{ambulance.pickupAddress}</span>
                            </div>
                            <div className="flex items-start">
                              <span className="font-medium text-gray-700 min-w-[80px]">Dropoff:</span>
                              <span className="text-gray-600 flex-1">{ambulance.dropoffAddress}</span>
                            </div>
                          </div>
                        </div>

                        {/* Vehicle Details */}
                        <div className="space-y-2 bg-white/50 rounded-lg p-2 sm:p-3">
                          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-2 sm:space-y-0">
                            <h4 className="font-semibold text-xs sm:text-sm flex items-center">
                              <Activity className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
                              <span className="whitespace-nowrap">Vehicle Details <span className="text-gray-600 font-normal">(mock_data)</span></span>
                            </h4>
                            <div className="flex flex-wrap items-center gap-2 sm:gap-3 text-xs">
                              <div className="flex items-center space-x-1">
                                <Camera className="h-3 w-3" />
                                <span className="hidden sm:inline">Dash Camera</span>
                                <span className="sm:hidden">Dash</span>
                              </div>
                              <div className="flex items-center space-x-1">
                                <Camera className="h-3 w-3" />
                                <span className="hidden sm:inline">Driver Camera</span>
                                <span className="sm:hidden">Driver</span>
                              </div>
                              <div className="flex items-center space-x-1">
                                <Camera className="h-3 w-3" />
                                <span className="hidden sm:inline">Patient Camera</span>
                                <span className="sm:hidden">Patient</span>
                              </div>
                            </div>
                          </div>

                          {/* Sensor Data - Reading from mock data */}
                          {renderSensorDataGrid()}
                        </div>
                      </div>

                      {/* Right Column - Map */}
                      <div className="relative h-48 sm:h-56 lg:h-64 rounded-lg border border-gray-300 overflow-hidden bg-gray-100">
                        {originalBooking ? (
                          <TrackingMapWrapper
                            booking={originalBooking}
                            bookingId={ambulance.id}
                          />
                        ) : (
                          <TrackingMap
                            pickupLocation={ambulance.pickupAddress ? {
                              latitude: 23.0225,
                              longitude: 72.5714,
                              address: ambulance.pickupAddress
                            } : null}
                            dropoffLocation={ambulance.dropoffAddress ? {
                              latitude: 23.0225,
                              longitude: 72.5714,
                              address: ambulance.dropoffAddress
                            } : null}
                            ambulanceLocation={null}
                            journeyDirection={ambulance.journey_direction || ambulance.journeyDirection || 'OUTBOUND'}
                            zoom={14}
                            showControls={false}
                            height="100%"
                          />
                        )}
                      </div>
                    </div>
                  </div>
                );
                  })
                )}
              </div>
            </div>
          )}

          {/* Right Column - Hidden (only left column with individual maps) */}
          <div className="hidden">
            {/* Removed general map view - each ambulance now has its own map */}
            {/* <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">System Performance</h3>
                <Gauge className="h-6 w-6 text-blue-600" />
              </div>
              
              <div className="flex items-center justify-center">
                <div className="relative w-32 h-32">
                 
                  <svg className="w-32 h-32 transform -rotate-90" viewBox="0 0 36 36">
                    <path
                      className="text-gray-200"
                      stroke="currentColor"
                      strokeWidth="3"
                      fill="none"
                      d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                    />
                    <path
                      className="text-blue-600"
                      stroke="currentColor"
                      strokeWidth="3"
                      strokeLinecap="round"
                      fill="none"
                      strokeDasharray="75, 100"
                      d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                    />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-2xl font-bold text-gray-900">75%</span>
                  </div>
                </div>
              </div>
              
              <div className="mt-4 text-center">
                <p className="text-sm text-gray-600">Overall System Efficiency</p>
                <div className="mt-2 flex justify-center space-x-4 text-xs">
                  <span className="flex items-center">
                    <div className="w-2 h-2 bg-green-500 rounded-full mr-1"></div>
                    Optimal: 85%
                  </span>
                  <span className="flex items-center">
                    <div className="w-2 h-2 bg-yellow-500 rounded-full mr-1"></div>
                    Good: 70%
                  </span>
                  <span className="flex items-center">
                    <div className="w-2 h-2 bg-red-500 rounded-full mr-1"></div>
                    Poor: 50%
                  </span>
                </div>
              </div>
            </div> */}

            {/* Map View Section - Individual maps shown in each ambulance card */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Live Map View</h3>
                <Map className="h-6 w-6 text-blue-600" />
              </div>

              <div className="relative bg-gray-100 rounded-lg h-96 flex items-center justify-center">
                <div className="text-center p-8">
                  <Map className="h-16 w-16 mx-auto mb-4 text-gray-400" />
                  <p className="text-gray-600 font-medium mb-2">Individual Maps Available</p>
                  <p className="text-sm text-gray-500">
                    Each ambulance card below displays its own live map with driver location, pickup, and dropoff points.
                  </p>
                  <p className="text-xs text-gray-400 mt-2">
                    Active Vehicles: {metrics.onDuty} | Total Bookings: {metrics.vehicles}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SAdminDashboard;
